import { useState } from 'react'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useMutation } from '@apollo/client'
import UPDATE_NONPROFIT_ORG_PROFILE from 'Apollo/Mutations/UpdateNonprofitOrgProfile'
import { useAppState } from 'Context/AppContext/AppContext'
import { Button } from '@resiliatech/birdsong'
import { IntakeFlowStep } from '../../constants'
import {
  SummaryContainer,
  EditButton,
  SummaryText,
  StyledTextField,
} from './OrgSummary.styled'
import { ActionContainer, Container } from '../../IntakeFlow.styled'

type Props = {
  onNextStep: (nextStep: IntakeFlowStep) => void
  onBackStep: () => void
  orgSummary: string
  onUpdateOrgSummary: (summary: string) => void
  onSaveOrgSummary?: (summary: string) => void
}

export const OrgSummary = ({
  onNextStep,
  onBackStep,
  orgSummary,
  onUpdateOrgSummary,
  onSaveOrgSummary,
}: Props) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedSummary, setEditedSummary] = useState(orgSummary)
  const { nonprofitOrgConfig } = useAppState()
  const { orgId: nonprofitOrgId } = nonprofitOrgConfig

  const [updateNonprofitOrgProfile] = useMutation(
    UPDATE_NONPROFIT_ORG_PROFILE,
    {
      onError: (error) => {
        console.error('Error updating nonprofit profile:', error)
      },
    }
  )

  const handleEdit = () => {
    setIsEditing(true)
    setEditedSummary(orgSummary)
  }

  const handleSave = () => {
    // Update parent local state
    onUpdateOrgSummary(editedSummary)

    if (onSaveOrgSummary) {
      onSaveOrgSummary(editedSummary)
    }

    setIsEditing(false)
  }

  const handleCancel = () => {
    setIsEditing(false)
    // Reset to original value
    setEditedSummary(orgSummary)
  }

  const handleNext = () => {
    if (isEditing) {
      handleSave()
    }

    onNextStep(IntakeFlowStep.IMPACT_AREAS)

    // Update the confirmed org summary
    if (nonprofitOrgId) {
      updateNonprofitOrgProfile({
        variables: {
          updateNonprofitOrgProfileInput: {
            nonprofitOrgId,
            confirmedOrgSummary: orgSummary,
          },
        },
      }).catch((error) => {
        console.error('Error updating nonprofit profile:', error)
      })
    }
  }

  return (
    <Container>
      <SummaryContainer isEditing={isEditing}>
        {!isEditing ? (
          <>
            <SummaryText>{orgSummary}</SummaryText>
            <EditButton
              onClick={handleEdit}
              size='small'
              aria-label='Edit summary'
            >
              <EditOutlinedIcon />
            </EditButton>
          </>
        ) : (
          <StyledTextField
            multiline
            rows={4}
            variant='outlined'
            value={editedSummary}
            onChange={(e) => setEditedSummary(e.target.value)}
            placeholder='Edit your organization summary...'
          />
        )}
      </SummaryContainer>

      {!isEditing && (
        <ActionContainer>
          <Button variant='text' onClick={onBackStep}>
            Back
          </Button>
          <Button variant='primary' onClick={handleNext} label='Next' />
        </ActionContainer>
      )}
      {isEditing && (
        <ActionContainer>
          <Button variant='text' onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSave} label='Save' />
        </ActionContainer>
      )}
    </Container>
  )
}
